import { Action, createReducer, on } from "@ngrx/store";
import * as loginFormActions from "../actions/login-form.action";

// reducer state model
export interface LoginFormState {
    bannerStatus: boolean;
    bannerMessage: string;
    bannerColor: string;
    sampleCenterOAuthSuccess: boolean;
}

// reducer initial state
export const initialState: LoginFormState = {
    bannerStatus: false,
    bannerMessage: "",
    bannerColor: "primary",
    sampleCenterOAuthSuccess: false
};

const loginFormReducer = createReducer(
    initialState,
    on(loginFormActions.userLoginAttempt, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage: "Attempting to log in.",
        bannerColor: "primary"
    })),
    on(loginFormActions.userLoginSuccess, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage: "User is being logged in."
    })),
    on(loginFormActions.userLoginFail, (state, { payload }) => {
        const errorPayload = payload;

        let bannerMessage = errorPayload.error.message;

        if (errorPayload.status !== 404 && !(<any>window).isDemo) {
            bannerMessage =
                "Passport is experiencing technical difficulties, " +
                "please try again in 5 minutes. [" +
                errorPayload.status +
                "]";
        } else if ((<any>window).isDemo) {
            bannerMessage = "Incorrect username or password. You may need to create a new MyCL account.";
        }

        return {
            ...state,
            bannerStatus: true,
            bannerMessage: bannerMessage,
            bannerColor: "warn"
        };
    }),
    on(loginFormActions.userCleverLoginAttempt, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage: "Attempting to log in with Clever.",
        bannerColor: "primary"
    })),
    on(loginFormActions.userCleverLoginFail, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage:
            "Failed to log in. Please try again. If problem continues, contact support.",
        bannerColor: "warn"
    })),
    on(loginFormActions.userCleverLoginNoToken, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage:
            "Failed to log in. No oAuth token found. Please try again. If problem continues, contact support.",
        bannerColor: "warn"
    })),
    on(loginFormActions.userClassLinkLoginAttempt, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage: "Attempting to log in with ClassLink.",
        bannerColor: "primary"
    })),
    on(loginFormActions.userClassLinkLoginFail, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage:
            "Failed to log in. Please try again. If problem continues, contact support.",
        bannerColor: "warn"
    })),
    on(loginFormActions.userClassLinkLoginNoToken, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage:
            "Failed to log in. No oAuth token found. Please try again. If problem continues, contact support.",
        bannerColor: "warn"
    })),
    on(loginFormActions.userSampleCenterLoginAttempt, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage: "Attempting to log in Sample Center User.",
        bannerColor: "primary",
        sampleCenterOAuthSuccess: false
    })),
    on(loginFormActions.userSampleCenterLoginFail, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage:
            "Failed to log in. Please try again. If problem continues, contact support.",
        bannerColor: "warn"
    })),
    on(loginFormActions.userSampleCenterLoginSuccess, state => ({
        ...state,
        sampleCenterOAuthSuccess: true
    })),
    on(loginFormActions.userSampleCenterLoginNoToken, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage:
            "Failed to log in. No oAuth token found. Please try again. If problem continues, contact support.",
        bannerColor: "warn"
    })),
    on(loginFormActions.userSampleCenterRedirect, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage: "Redirecting back to Sample Center, please wait...",
        bannerColor: "primary"
    })),
    on(loginFormActions.userMyCLRedirect, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage: "Redirecting back to login, please wait...",
        bannerColor: "primary"
    })),
    on(loginFormActions.userLogout, state => ({
        ...state,
        bannerStatus: false,
        bannerMessage: "",
        bannerColor: "primary"
    })),
    on(loginFormActions.userRedirect, state => ({
        // Triggered by effect
        ...state,
        bannerStatus: false,
        bannerMessage: "",
        bannerColor: "primary"
    })),
    on(loginFormActions.userProfileAttempt, state => ({
        // Triggered by effect
        ...state,
        bannerStatus: true,
        bannerMessage: "Retrieving User profile.",
        bannerColor: "primary"
    })),
    on(loginFormActions.userProfileSuccess, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage: "User profile retrieved.",
        bannerColor: "primary"
    })),
    on(loginFormActions.userProfileFail, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage:
            "Passport is experiencing technical difficulties, " +
            "please try again in 5 minutes. [Profile]",
        bannerColor: "warn"
    })),
    on(loginFormActions.userLoginMaintenance, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage:
            "Passport is currently undergoing scheduled maintenance. We'll be back soon." +
            " Thank you for your patience!",
        bannerColor: "warn"
    })),
    on(loginFormActions.http503, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage: "Redirected to log in, server unavailable.",
        bannerColor: "warn"
    })),
    on(loginFormActions.http403, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage: "Session ended, please login again.",
        bannerColor: "primary"
    })),
    on(loginFormActions.noAction, state => ({
        ...state
    })),
    on(loginFormActions.userLoginResetState, state => {
        return initialState;
    }),
    on(loginFormActions.userResetPwdNoHash, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage:
            "No valid hash found. Ensure URL is correct. To reset your password again, please click the 'Forgot Password' link on the login page.",
        bannerColor: "warn"
    })),
    on(loginFormActions.userResetPwdAttempt, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage: "Attempting to reset password.",
        bannerColor: "primary"
    })),
    on(loginFormActions.userResetPwdSuccess, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage:
            "Password successfully reset, redirecting to login page.",
        bannerColor: "primary"
    })),
    on(loginFormActions.userResetPwdFail, state => ({
        ...state,
        bannerStatus: true,
        bannerMessage:
            "This link is no longer valid. To reset your password again, please click the 'Forgot Password' link on the login page.",
        bannerColor: "warn"
    }))
);

// exporting the reducer so it can be added to the index file
export function reducer(state: LoginFormState | undefined, action: Action) {
    return loginFormReducer(state, action);
}

// export individual state levels
export const getLoginBannerStatus = (state: LoginFormState) =>
    state.bannerStatus;
export const getLoginBannerMessage = (state: LoginFormState) =>
    state.bannerMessage;
export const getLoginBannerColor = (state: LoginFormState) => state.bannerColor;
export const getSampleCenterOAuthSuccess = (state: LoginFormState) => state.sampleCenterOAuthSuccess;
